import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, ScanLine, PackageOpen } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@atoms/Input';
import { Button } from '@atoms/Button';
import { BarcodeScanner } from '@molecules/BarcodeScanner';
import { Spinner } from '@atoms/Spinner';
import { ProductCard } from '@molecules/ProductCard';
import { WholesalerCard } from '@molecules/WholesalerCard';
import { SectionTitle } from '@atoms/SectionTitle';
import { searchService } from '@services/searchService';
import { useAuthStore } from '@store/authStore';
import { useAddToCart } from '@/hooks/useAddToCart';
import type { Product } from '@/types';

export function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get('q') ?? '';
  const [query, setQuery] = useState(initialQ);
  const [showScanner, setShowScanner] = useState(false);
  const { isAuthenticated, user } = useAuthStore();
  const { requestAdd } = useAddToCart('/restaurant?tab=browse');

  useEffect(() => {
    setQuery(initialQ);
  }, [initialQ]);

  const { data, isFetching } = useQuery({
    queryKey: ['search', 'page', initialQ],
    queryFn: () => searchService.search(initialQ),
    enabled: initialQ.trim().length >= 2,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length < 2) return;
    setSearchParams({ q: trimmed }, { replace: true });
  };

  const handleAdd = (product: Product) => {
    requestAdd(product);
  };

  const goWholesaler = (id: string) => {
    if (isAuthenticated && user?.role === 'restaurant') {
      navigate(`/restaurant?tab=browse&wholesaler=${id}`);
      return;
    }
    navigate(`/login?redirect=${encodeURIComponent(`/restaurant?tab=browse&wholesaler=${id}`)}`);
  };

  const hasQuery = initialQ.trim().length >= 2;
  const hasResults = Boolean(data?.products?.length || data?.wholesalers?.length);

  return (
    <div className="section-container space-y-8 py-8 md:py-12">
      <div>
        <Link to="/" className="text-sm font-medium text-brand-600 hover:text-brand-700">
          ← Ana sayfa
        </Link>
        <SectionTitle
          className="mb-0 mt-4"
          title="Ürün ve Toptancı "
          accent="Arama"
          subtitle="Katalog genelinde ürün, kategori veya toptancı arayın."
        />
      </div>

      <form onSubmit={handleSubmit} className="card-elevated flex flex-col gap-3 p-4 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ürün, kategori veya toptancı ara..."
            autoFocus
          />
        </div>
        <div className="flex gap-2">
          <Button type="submit">
            <Search className="h-4 w-4" />
            Ara
          </Button>
          <Button type="button" variant="outline" onClick={() => setShowScanner(true)} aria-label="Barkod tara">
            <ScanLine className="h-4 w-4" />
          </Button>
        </div>
      </form>

      <BarcodeScanner
        open={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={(code) => {
          setQuery(code);
          setSearchParams({ q: code }, { replace: true });
        }}
      />

      {!hasQuery ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center">
          <Search className="h-10 w-10 text-slate-300" strokeWidth={1.25} />
          <p className="mt-4 text-base font-medium text-slate-700">Aramaya başlayın</p>
          <p className="mt-1 text-sm text-slate-500">En az 2 karakter girerek arama yapabilirsiniz.</p>
        </div>
      ) : isFetching ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-slate-500">Sonuçlar aranıyor...</p>
        </div>
      ) : !hasResults ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 text-center">
          <PackageOpen className="h-10 w-10 text-slate-300" strokeWidth={1.25} />
          <p className="mt-4 text-base font-medium text-slate-700">Sonuç bulunamadı</p>
          <p className="mt-1 text-sm text-slate-500">
            &ldquo;{initialQ}&rdquo; için eşleşen ürün veya toptancı yok.
          </p>
        </div>
      ) : (
        <div className="space-y-12">
          {data?.products?.length ? (
            <section>
              <h2 className="mb-6 text-lg font-semibold text-slate-900">
                Ürünler
                <span className="ml-2 text-sm font-normal text-slate-500">({data.products.length})</span>
              </h2>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {data.products.map((product) => (
                  <ProductCard key={product.id} product={product} onAdd={handleAdd} showWholesaler />
                ))}
              </div>
            </section>
          ) : null}

          {data?.wholesalers?.length ? (
            <section>
              <h2 className="mb-6 text-lg font-semibold text-slate-900">
                Toptancılar
                <span className="ml-2 text-sm font-normal text-slate-500">({data.wholesalers.length})</span>
              </h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {data.wholesalers.map((w) => (
                  <WholesalerCard key={w.id} wholesaler={w} onClick={() => goWholesaler(w.id)} />
                ))}
              </div>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}
